import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Globe, ShoppingCart, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';

export const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { language, toggleLanguage, t } = useLanguage();
  const { cart } = useCart();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { goto: id } });
      setIsMobileMenuOpen(false);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isScrolled 
          ? 'bg-card backdrop-blur-md shadow-md' 
          : 'bg-card'
      }`}
      style={{ top: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('hero')}>
            <img 
              src="/images/Amir-Pizza-Logo.png" 
              alt="Amir's Pizza" 
              width="48"
              height="48"
              className="h-12 w-12 object-contain"
            />
            {/* <span className="ml-2 text-xl font-bold text-primary">Amir's Pizza</span> */}
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => navigate('/')} 
              className="text-foreground hover:text-primary transition-colors font-medium"
            >
              {(t('home') || 'Home').toUpperCase()}
            </button>
            <button 
              onClick={() => scrollToSection('menu')} 
              className="text-foreground hover:text-primary transition-colors font-medium"
            >
              {t('menu')}
            </button>
            <button 
              onClick={() => scrollToSection('offers')} 
              className="text-foreground hover:text-primary transition-colors font-medium"
            >
              {t('offers')}
            </button>
            <button 
              onClick={() => scrollToSection('contact')} 
              className="text-foreground hover:text-primary transition-colors font-medium"
            >
              {t('contact')}
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-3">
            {/* Theme Toggle */}
            <label htmlFor="theme-switch" className="switch" aria-label="Toggle theme">
              <input
                id="theme-switch"
                type="checkbox"
                checked={theme === 'dark'}
                onChange={toggleTheme}
              />
              <span className="slider"></span>
              <span className="decoration"></span>
            </label>

            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLanguage}
              className="rounded-full"
              aria-label={`Switch language. Current: ${language.toUpperCase()}`}
            >
              <Globe className="h-5 w-5" />
              <span className="ml-1 text-xs">{language.toUpperCase()}</span>
            </Button>

            {/* Cart */}
            <Button
              onClick={() => navigate('/cart')}
              className="relative bg-primary hover:bg-primary/90 text-primary-foreground"
              aria-label="Open cart"
            >
              <ShoppingCart className="h-5 w-5" />
              {cart.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-secondary text-secondary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                  {cart.length}
                </span>
              )}
            </Button>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden"
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden pb-4 animate-fade-in">
            <div className="flex flex-col space-y-3">
              <button 
                onClick={() => navigate('/')} 
                className="text-left px-4 py-2 text-foreground hover:bg-muted rounded-lg transition-colors"
              >
                {(t('home') || 'Home').toUpperCase()}
              </button>
              <button 
                onClick={() => scrollToSection('menu')} 
                className="text-left px-4 py-2 text-foreground hover:bg-muted rounded-lg transition-colors"
              >
                {t('menu')}
              </button>
              <button 
                onClick={() => scrollToSection('offers')} 
                className="text-left px-4 py-2 text-foreground hover:bg-muted rounded-lg transition-colors"
              >
                {t('offers')}
              </button>
              <button 
                onClick={() => scrollToSection('contact')} 
                className="text-left px-4 py-2 text-foreground hover:bg-muted rounded-lg transition-colors"
              >
                {t('contact')}
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
